import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Loader2, Video } from "lucide-react";

/** Öffentliche Video-Beratung (Jitsi Meet) — Kunde tritt ohne Login bei. */
export default function PublicVideoMeetingPage() {
    const { token } = useParams();
    const [meeting, setMeeting] = useState(null);
    const [error, setError] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [joined, setJoined] = useState(false);
    const containerRef = useRef(null);
    const apiRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/video/${token}`);
                setMeeting(data);
                setDisplayName(data.customer_name || "");
            } catch (e) {
                setError(e.response?.data?.detail || "Diese Video-Beratung ist nicht (mehr) verfügbar.");
            }
        })();
        return () => { apiRef.current?.dispose?.(); };
    }, [token]);

    const join = () => {
        if (!meeting) return;
        setJoined(true);
        const start = () => {
            /* global JitsiMeetExternalAPI */
            apiRef.current = new window.JitsiMeetExternalAPI(meeting.jitsi_base.replace(/^https?:\/\//, ""), {
                roomName: meeting.room,
                parentNode: containerRef.current,
                userInfo: { displayName: displayName || "Gast" },
                configOverwrite: { prejoinPageEnabled: false, disableDeepLinking: true },
                interfaceConfigOverwrite: { SHOW_JITSI_WATERMARK: false, MOBILE_APP_PROMO: false },
            });
        };
        if (window.JitsiMeetExternalAPI) { setTimeout(start, 50); return; }
        const s = document.createElement("script");
        s.src = `${meeting.jitsi_base}/external_api.js`;
        s.onload = () => setTimeout(start, 50);
        s.onerror = () => setError("Video-Dienst konnte nicht geladen werden — bitte Seite neu laden.");
        document.body.appendChild(s);
    };

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#111827] text-white p-6" data-testid="public-video-error">
                <div className="text-center max-w-md">
                    <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-bold">{error}</p>
                </div>
            </div>
        );
    }
    if (!meeting) {
        return <div className="min-h-screen flex items-center justify-center bg-[#111827] text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;
    }

    return (
        <div className="min-h-screen bg-[#111827] flex flex-col" data-testid="public-video-page">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                {meeting.dealer_logo ? (
                    <img src={meeting.dealer_logo} alt="" className="h-8 w-auto object-contain bg-white rounded-sm px-1" />
                ) : null}
                <div>
                    <div className="text-white font-bold text-sm" data-testid="public-video-title">{meeting.title}</div>
                    <div className="text-white/60 text-xs">{meeting.dealer_name}</div>
                </div>
            </div>
            {!joined ? (
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="bg-white rounded-sm p-6 w-full max-w-sm text-center">
                        <Video className="w-10 h-10 mx-auto mb-3 text-[#111827]" />
                        <h1 className="font-black text-lg text-[#111827] mb-1">Video-Beratung</h1>
                        <p className="text-xs text-gray-500 mb-4">Bitte Kamera & Mikrofon freigeben, wenn der Browser danach fragt.</p>
                        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Ihr Name"
                            className="w-full h-10 border border-gray-300 rounded-sm px-3 text-sm mb-3 focus:outline-none focus:border-[#111827]" data-testid="public-video-name" />
                        {meeting.vehicle && (
                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-sm p-2 mb-3 text-left" data-testid="public-video-vehicle">
                                {meeting.vehicle.image ? <img src={meeting.vehicle.image} alt="" className="w-14 h-14 object-cover rounded-sm shrink-0" /> : null}
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-bold truncate">{meeting.vehicle.title}</div>
                                    {meeting.vehicle.price ? <div className="text-xs text-gray-500">{Number(meeting.vehicle.price).toLocaleString("de-DE")} €</div> : null}
                                    <a href={`/listing/${meeting.vehicle.id}`} target="_blank" rel="noreferrer" className="text-[11px] font-bold underline" data-testid="public-video-vehicle-link">Exposé ansehen</a>
                                </div>
                            </div>
                        )}
                        <button type="button" onClick={join} className="w-full h-11 bg-[#111827] text-[#FFCC00] font-bold rounded-sm hover:bg-black" data-testid="public-video-join">
                            Beratung beitreten
                        </button>
                    </div>
                </div>
            ) : (
                <div ref={containerRef} className="flex-1 [&>iframe]:w-full [&>iframe]:h-full" style={{ minHeight: "80vh" }} data-testid="public-video-frame" />
            )}
        </div>
    );
}
